import {
  TOOL_ACTIVITY_COPY,
  ACTIVITY_WIDGET_LABELS,
} from "./tool-activity-copy";
import { useRef, useState, type ReactElement } from "react";
import {
  TriangleAlert,
  Check,
  ChevronRight,
  FileText,
  LayoutDashboard,
  Maximize2,
  ShieldCheck,
  X,
} from "lucide-react";
import {
  PriorityFlag,
  Sparkline,
  Spinner,
  StatusDot,
  TrendIndicator,
  type StatusTone,
} from "../atoms/index.js";
import {
  Card,
  CardBody,
  Timeline,
  Tooltip,
  TruncatedText,
} from "../molecules/index.js";
import { CopyErrorDetailsButton } from "./CopyErrorDetailsButton";
import { renderHoshinProposalCard } from "./HoshinProposalCards";
import { CanvasProposalPreview } from "./CanvasProposalPreview";
import {
  ProposalCanvasDestinationButton,
  ProposalCanvasDestinationNotice,
} from "./ProposalCanvasDestination";
import { ProposalCardFrame } from "./ProposalCardFrame";
import { ProposalChangePreview } from "./ProposalChangePreview";
import { ProposalOutcomeNotice } from "./ProposalOutcomeNotice";
import { proposalApplyOutcome } from "./proposal-apply-outcome.js";
import { useCanvasDelivery } from "./CanvasDeliveryNotice.js";
import {
  proposalExpired,
  useProposalDeadlineClock,
} from "./proposal-expiry.js";
import type {
  KnownPlayerAIRunActivityKind,
  PlayerAIDocumentGroundingPayload,
  PlayerAIIntegrationService,
  PlayerAIActionBatchPayload,
  PlayerAIKpiListPayload,
  PlayerAIKpiRow,
  PlayerAIProposalCanvasDestination,
  PlayerAIProposalCanvasRevealOutcome,
  PlayerAIQuickReplyPayload,
  PlayerAIRunActivityPayload,
  PlayerAIRunActivityRow,
  PlayerAISignalPayload,
  PlayerAITaskListPayload,
  PlayerAITaskRow,
  PlayerAIToolProposalPayload,
  PlayerAIUnresolvedSources,
  PlayerAIVideoPayload,
  PlayerAIWidgetActionHandler,
  PlayerAIWidgetPayload,
  PlayerAIWidgetTone,
} from "./playerai.types";

function sparkToneFor(t: PlayerAIWidgetTone | undefined) {
  switch (t) {
    case "critical":
      return "critical" as const;
    case "watch":
      return "watch" as const;
    case "info":
      return "info" as const;
    case "purple":
      return "ai" as const;
    case "neutral":
    case "healthy":
    default:
      return "healthy" as const;
  }
}

/* ---------------- Platform action batch ---------------- */

export function ActionBatchWidget({
  payload,
  onAction,
}: {
  payload: PlayerAIActionBatchPayload;
  onAction?: PlayerAIWidgetActionHandler;
}) {
  const [editing, setEditing] = useState(false);
  const flatActions = payload.groups.flatMap((group) => group.actions);
  const [drafts, setDrafts] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      flatActions.map((action) => [
        action.actionId,
        JSON.stringify(action.input, null, 2),
      ]),
    ),
  );
  const invalidDraft = flatActions.some((action) => {
    try {
      const value = JSON.parse(drafts[action.actionId] ?? "{}");
      return (
        typeof value !== "object" || value === null || Array.isArray(value)
      );
    } catch {
      return true;
    }
  });
  const statusLabel: Record<PlayerAIActionBatchPayload["status"], string> = {
    pending_confirmation: "Needs confirmation",
    applying: "Applying",
    applied: "Applied",
    partially_applied: "Partially applied",
    failed: "Failed",
    invalidated: "Changed",
  };

  return (
    <section className="mt-2 w-full rounded-md border border-pb-iris/30 bg-pb-card/95 p-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-xs font-semibold text-pb-text">
            Review {payload.actionCount} proposed{" "}
            {payload.actionCount === 1 ? "change" : "changes"}
          </div>
          <div className="mt-0.5 text-dense-2 text-pb-text-muted">
            {statusLabel[payload.status]} · preview {payload.revision}
          </div>
        </div>
        {payload.status === "applying" ? (
          /* The Spinner ring (OK-38), in the Player AI iris. */
          <Spinner size="md" tone="ai" aria-label="Applying changes" />
        ) : null}
      </div>

      <div className="mt-3 space-y-3">
        {payload.groups.map((group) => (
          <div key={group.owner}>
            <div className="text-dense-2 font-semibold uppercase tracking-wider text-pb-text-muted">
              {group.label}
            </div>
            <ul className="mt-1 space-y-1.5">
              {group.actions.map((action) => {
                const index = flatActions.findIndex(
                  (candidate) => candidate.actionId === action.actionId,
                );
                return (
                  /* The recessed row is Card inset sm (OK-22, SN-07). */
                  <li key={action.actionId}>
                    <Card variant="inset" size="sm">
                      <CardBody>
                        <div className="flex items-start gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="text-xs font-medium text-pb-text">
                              {action.label}
                            </div>
                            {action.status ? (
                              <div className="mt-0.5 text-dense-2 text-pb-text-muted">
                                {action.status === "applied"
                                  ? "Applied"
                                  : action.status === "untouched"
                                    ? "Not attempted"
                                    : action.message ||
                                      "Could not apply safely"}
                              </div>
                            ) : null}
                          </div>
                          {payload.canEdit &&
                          payload.actionCount > 1 &&
                          !editing ? (
                            <button
                              type="button"
                              className="text-dense-2 text-pb-text-muted hover:text-pb-red-text"
                              onClick={() =>
                                onAction?.(
                                  `actionBatch:remove:${payload.batchId}`,
                                  {
                                    kind: "platform_action_batch_remove",
                                    selections: [{ index, text: "" }],
                                  },
                                )
                              }
                            >
                              Remove
                            </button>
                          ) : null}
                        </div>
                        {editing ? (
                          <label className="mt-2 block text-dense-2 text-pb-text-muted">
                            Action details
                            <textarea
                              aria-label={`Edit ${action.label}`}
                              value={drafts[action.actionId] ?? "{}"}
                              rows={3}
                              onChange={(event) =>
                                setDrafts((current) => ({
                                  ...current,
                                  [action.actionId]: event.target.value,
                                }))
                              }
                              className="mt-1 w-full resize-y rounded border border-pb-control-border bg-pb-card px-2 py-1.5 font-mono text-dense-2 text-pb-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pb-iris"
                            />
                          </label>
                        ) : null}
                      </CardBody>
                    </Card>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>

      {invalidDraft && editing ? (
        <p className="mt-2 text-dense-2 text-pb-red-text">
          Each action detail must be a valid JSON object.
        </p>
      ) : null}

      {payload.status === "pending_confirmation" ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {payload.canEdit ? (
            editing ? (
              <>
                <button
                  type="button"
                  disabled={invalidDraft}
                  onClick={() => {
                    onAction?.(`actionBatch:edit:${payload.batchId}`, {
                      kind: "platform_action_batch_edit",
                      selections: flatActions.map((action, index) => ({
                        index,
                        text: drafts[action.actionId] ?? "{}",
                      })),
                    });
                    setEditing(false);
                  }}
                  className="rounded-md border border-pb-control-border bg-pb-card px-2 py-1 text-dense-1 font-medium text-pb-text disabled:opacity-50"
                >
                  Update preview
                </button>
                <button
                  type="button"
                  onClick={() => setEditing(false)}
                  className="rounded-md px-2 py-1 text-dense-1 text-pb-text-muted hover:text-pb-text"
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="rounded-md border border-pb-control-border bg-pb-card px-2 py-1 text-dense-1 font-medium text-pb-text-secondary"
              >
                Edit details
              </button>
            )
          ) : null}
          {!editing && payload.canApply ? (
            <button
              type="button"
              onClick={() => onAction?.(`actionBatch:apply:${payload.batchId}`)}
              className="rounded-md bg-pb-accent px-2 py-1 text-dense-1 font-medium text-pb-accent-foreground hover:bg-pb-accent-hover"
            >
              Confirm and apply {payload.actionCount}{" "}
              {payload.actionCount === 1 ? "change" : "changes"}
            </button>
          ) : null}
        </div>
      ) : null}

      {payload.remainderBatchId ? (
        <button
          type="button"
          onClick={() =>
            onAction?.(`actionBatch:remainder:${payload.remainderBatchId}`)
          }
          className="mt-3 text-dense-1 font-medium text-pb-iris-text"
        >
          {payload.remainderActionCount === undefined
            ? "Review the remaining changes"
            : `Review the remaining ${payload.remainderActionCount} ${
                payload.remainderActionCount === 1 ? "change" : "changes"
              }`}
        </button>
      ) : null}
    </section>
  );
}

function dotTone(t: PlayerAIWidgetTone | undefined): StatusTone {
  // PlayerAIWidgetTone is the shell's data vocabulary (still uses "purple");
  // translate it to the atom's semantic tone vocabulary.
  return t === "purple" ? "ai" : (t ?? "healthy");
}

function FrameTitle({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-dense-2 uppercase tracking-wider text-pb-text-muted font-semibold mb-2">
      {children}
    </div>
  );
}

function FrameFooter({
  label,
  onSelect,
}: {
  label: string;
  onSelect?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className="mt-2 inline-flex items-center gap-0.5 text-dense-1 text-pb-iris-text hover:underline underline-offset-2 font-medium transition-colors"
    >
      {label}
      <ChevronRight className="w-3 h-3" />
    </button>
  );
}

/* ---------------- KPI list ---------------- */

function KpiRow({ row }: { row: PlayerAIKpiRow }) {
  return (
    <div className="flex items-center gap-2 min-w-0 py-1">
      <StatusDot tone={dotTone(row.tone)} size="xs" />
      <div className="flex-1 min-w-0">
        <div className="text-xs text-pb-text font-medium truncate leading-tight">
          {row.label}
        </div>
        <div className="text-dense-1 text-pb-text-muted pb-tabular leading-tight">
          {row.value}
          {row.unit ? <span className="ms-0.5">{row.unit}</span> : null}
        </div>
      </div>
      <Sparkline
        data={row.series}
        width={48}
        height={18}
        tone={sparkToneFor(row.tone)}
        className="shrink-0"
      />
      <TrendIndicator
        delta={row.delta}
        className="shrink-0 text-dense-1"
        {...(row.inverse !== undefined ? { inverse: row.inverse } : {})}
      />
    </div>
  );
}

export function KpiListWidget({
  payload,
}: {
  payload: PlayerAIKpiListPayload;
}) {
  return (
    /* The recessed widget frame is Card inset sm (OK-22, SN-07). */
    <Card variant="inset" size="sm" className="relative mt-2">
      <CardBody>
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0">
            {payload.title && <FrameTitle>{payload.title}</FrameTitle>}
          </div>
          <Tooltip content="Add to dashboard" describeChild={false}>
            <button
              type="button"
              onClick={payload.onAddToDashboard}
              aria-label="Add to dashboard"
              className="shrink-0 -mt-0.5 -me-0.5 w-6 h-6 flex items-center justify-center rounded text-pb-text-muted hover:text-pb-iris-text hover:bg-pb-iris-soft/60 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pb-iris"
            >
              <LayoutDashboard className="w-3.5 h-3.5" />
            </button>
          </Tooltip>
        </div>
        <div className="divide-y divide-pb-border/60">
          {payload.rows.map((r) => (
            <KpiRow key={r.id} row={r} />
          ))}
        </div>
        {payload.footerLink && (
          <FrameFooter
            label={payload.footerLink.label}
            {...(payload.footerLink.onSelect !== undefined
              ? { onSelect: payload.footerLink.onSelect }
              : {})}
          />
        )}
      </CardBody>
    </Card>
  );
}

/* ---------------- Task list ---------------- */

function TaskRow({ row }: { row: PlayerAITaskRow }) {
  return (
    <div className="flex items-center gap-2 min-w-0 py-1.5">
      <PriorityFlag level={row.priority} className="shrink-0" />
      <div className="flex-1 min-w-0 text-xs text-pb-text leading-tight truncate">
        {row.title}
      </div>
      {row.due && (
        <div className="shrink-0 text-dense-1 text-pb-text-muted pb-tabular">
          {row.due}
        </div>
      )}
    </div>
  );
}

export function TaskListWidget({
  payload,
}: {
  payload: PlayerAITaskListPayload;
}) {
  return (
    /* The recessed widget frame is Card inset sm (OK-22, SN-07). */
    <Card variant="inset" size="sm" className="mt-2">
      <CardBody>
        {payload.title && <FrameTitle>{payload.title}</FrameTitle>}
        <div className="divide-y divide-pb-border/60">
          {payload.rows.map((r) => (
            <TaskRow key={r.id} row={r} />
          ))}
        </div>
        {payload.footerLink && (
          <FrameFooter
            label={payload.footerLink.label}
            {...(payload.footerLink.onSelect !== undefined
              ? { onSelect: payload.footerLink.onSelect }
              : {})}
          />
        )}
      </CardBody>
    </Card>
  );
}

/* ---------------- Signal callout ---------------- */

const SIGNAL_BG: Record<PlayerAIWidgetTone, string> = {
  critical: "border-pb-red/40 bg-pb-red-soft",
  watch: "border-pb-amber/40 bg-pb-amber-soft",
  healthy: "border-pb-green/40 bg-pb-green-soft",
  info: "border-pb-cyan/40 bg-pb-cyan-soft",
  purple: "border-pb-iris/40 bg-pb-iris-soft",
  neutral: "border-pb-border bg-pb-card",
};

export function SignalWidget({ payload }: { payload: PlayerAISignalPayload }) {
  const tone = payload.tone ?? "watch";
  return (
    <div className={`mt-2 rounded-md border p-2.5 ${SIGNAL_BG[tone]}`}>
      <div className="flex items-start gap-2">
        <StatusDot tone={dotTone(tone)} size="md" className="mt-1" />
        <div className="min-w-0">
          <div className="text-xs font-semibold text-pb-text leading-snug">
            {payload.headline}
          </div>
          {payload.detail && (
            <div className="text-dense-1 text-pb-text-secondary leading-snug mt-0.5">
              {payload.detail}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Quick reply buttons ---------------- */

export function QuickReplyWidget({
  payload,
  onAction,
}: {
  payload: PlayerAIQuickReplyPayload;
  onAction?: PlayerAIWidgetActionHandler;
}) {
  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {payload.buttons.map((button, index) => (
        <button
          key={`${index}-${button.action}`}
          type="button"
          onClick={() => onAction?.(button.action)}
          className="inline-flex items-center text-start text-xs bg-pb-card border border-pb-iris/40 hover:border-pb-iris/70 hover:bg-pb-iris-soft/30 text-pb-text rounded-full px-3 py-1.5 transition-colors font-medium"
        >
          {button.label}
        </button>
      ))}
    </div>
  );
}

/* ---------------- Tool proposal ---------------- */

function SingleWidgetCanvasDestination({
  destinations,
  onViewOnCanvas,
}: {
  readonly destinations:
    | readonly PlayerAIProposalCanvasDestination[]
    | undefined;
  readonly onViewOnCanvas:
    | ((widgetId: string) => PlayerAIProposalCanvasRevealOutcome)
    | undefined;
}) {
  const [outcome, setOutcome] =
    useState<PlayerAIProposalCanvasRevealOutcome | null>(null);
  const destination = destinations?.[0];
  if (!destination || !onViewOnCanvas) return null;
  return (
    <div className="mt-2 min-w-0">
      <div className="flex items-center gap-2">
        <span className="min-w-0 flex-1 truncate text-dense-1 text-pb-text-secondary">
          {destination.label}
          {destination.phase ? (
            <span className="ms-2 text-pb-text-muted">{destination.phase}</span>
          ) : null}
        </span>
        <ProposalCanvasDestinationButton
          destination={destination}
          onViewOnCanvas={onViewOnCanvas}
          onOutcome={setOutcome}
        />
      </div>
      <ProposalCanvasDestinationNotice outcome={outcome} />
    </div>
  );
}

export function ToolProposalWidget({
  payload: originalPayload,
  onAction,
}: {
  payload: PlayerAIToolProposalPayload;
  onAction?: PlayerAIWidgetActionHandler;
}) {
  const now = useProposalDeadlineClock(
    originalPayload.status === "pending" ? [originalPayload.expiresAt] : [],
  );
  const payload = proposalExpired(
    originalPayload.status,
    originalPayload.expiresAt,
    now,
  )
    ? {
        ...originalPayload,
        status: "expired" as const,
        unsupportedReviewForm: false,
      }
    : originalPayload;
  const canvasDelivery = useCanvasDelivery(payload.canvasDelivery);
  const canvasConfirmed =
    canvasDelivery.state === undefined || canvasDelivery.state === "applied";
  const canDecide = payload.canDecide ?? true;
  // An unreviewable proposal offers ONE action, and neither of these is it.
  // Suppressed here rather than at the host so a caller that forgets to clear
  // them cannot reintroduce "approve a write you cannot see".
  const unsupported = payload.unsupportedReviewForm === true;
  const canApprove =
    !unsupported &&
    (payload.canApprove ?? canDecide) &&
    payload.status === "pending";
  const canReject =
    !unsupported &&
    (payload.canReject ?? canDecide) &&
    payload.status === "pending";
  // The Hoshin workshop Proposals get dedicated, structured cards. Everyone in
  // the shared chat sees the same content; only the deciding driver gets the
  // Apply / Regenerate / Discard controls (via canApprove/canReject). The card
  // is resolved through a registry, so a new preview kind is one key rather
  // than another branch here.
  const hoshinCard = renderHoshinProposalCard({
    payload,
    canApprove,
    canReject,
    ...(onAction !== undefined ? { onAction } : {}),
  });
  if (hoshinCard) return <>{hoshinCard}</>;
  // A governed external write gets the compact integration card: service,
  // target, effect count, risk/state and `Review suggestion` — and deliberately
  // NOT the raw argument dump below, which is what the review dialog replaces.
  // The recovery state rides the same card whatever the host classified: an
  // unreviewable suggestion must offer its one action wherever it lands,
  // never a card with nothing to press while a Run waits on it.
  if (payload.service !== undefined || unsupported) {
    return (
      <IntegrationProposalCard
        payload={payload}
        canApprove={canApprove}
        canReject={canReject}
        {...(onAction !== undefined ? { onAction } : {})}
      />
    );
  }
  const isCanvasProposal =
    payload.preview?.kind === "canvas_bundle" ||
    /^canvas\.widget_changes\.apply(?:@\d+)?$/.test(payload.toolId);
  /* PB-4923 AC6 — "applied" is routinely a PARTIAL truth: the executor judges
     each operation on its own, so three causes can land while a fourth is left
     alone because a colleague is in that field. The result carrying that detail
     already reached this payload as `executionResult` and nothing read it, so
     the skipped part was invisible.

     One fold for both shapes, so the single-widget card and the canvas bundle
     cannot disagree about the same outcome. `null` when there is nothing to
     report — a clean apply renders no notice, because the status pill has
     already said Applied. */
  const outcome = proposalApplyOutcome(payload.executionResult, {
    params: payload.appliedParams,
    ...(payload.paramsForWidget !== undefined
      ? { paramsForWidget: payload.paramsForWidget }
      : {}),
    ...(payload.nameForActor !== undefined
      ? { nameForActor: payload.nameForActor }
      : {}),
  });
  return (
    <ProposalCardFrame
      payload={payload}
      {...(canvasDelivery.state !== undefined
        ? { canvasDeliveryState: canvasDelivery.state }
        : {})}
    >
      {!isCanvasProposal &&
      payload.description &&
      !/internal product action|@[0-9]+/.test(payload.description) ? (
        <div className="mt-1.5 text-dense-1 leading-snug text-pb-text-secondary">
          {payload.description}
        </div>
      ) : null}
      {payload.expiresAt && payload.status === "pending" ? (
        <div className="mt-1 text-dense-2 text-pb-text-muted">
          Expires {new Date(payload.expiresAt).toLocaleTimeString()}
        </div>
      ) : null}
      {isCanvasProposal && (
        <CanvasProposalPreview
          preview={payload.preview}
          {...(canvasConfirmed && payload.canvasDestinations !== undefined
            ? { destinations: payload.canvasDestinations }
            : {})}
          {...(canvasConfirmed && payload.onViewOnCanvas !== undefined
            ? { onViewOnCanvas: payload.onViewOnCanvas }
            : {})}
        />
      )}
      {/* Task 9 — a single-widget proposal has ONE destination, and its card
          has no per-widget row to hang the action on: the card IS the row. So
          it sits above the change detail rather than inside a disclosure that
          only exists for a bundle. */}
      {!isCanvasProposal && canvasConfirmed && (
        <SingleWidgetCanvasDestination
          destinations={payload.canvasDestinations}
          onViewOnCanvas={payload.onViewOnCanvas}
        />
      )}
      {!isCanvasProposal && <ProposalChangePreview payload={payload} />}
      {payload.error || payload.conflictNotice ? (
        <div className="mt-2 rounded border border-pb-red/30 bg-pb-red-soft px-2 py-1.5 text-dense-1 leading-snug text-pb-red-text">
          {/* A canvas apply failure is normally a raw code, so this card shows
              fixed guidance rather than the payload. `conflictNotice` is the
              exception it exists for: the ONE refusal that names which field
              and who, which must not be flattened into "could not be applied".
              Preferred on both branches, so the two cannot diverge. */}
          {payload.conflictNotice ??
            (isCanvasProposal
              ? "These changes could not be applied. Request a fresh canvas proposal and review it again."
              : payload.error)}
          {payload.errorDetails ? (
            <div className="mt-1.5">
              <CopyErrorDetailsButton
                details={payload.errorDetails}
                className="inline-flex items-center gap-1 rounded border border-pb-red/30 px-1.5 py-0.5 text-dense-2 text-pb-red-text transition-colors hover:bg-pb-red/10"
              />
            </div>
          ) : null}
        </div>
      ) : null}
      {outcome ? <ProposalOutcomeNotice outcome={outcome} /> : null}
      {canvasDelivery.notice}
      {(canApprove || canReject) && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {canApprove && (
            <button
              type="button"
              onClick={() =>
                onAction?.(`toolProposal:approve:${payload.proposalId}`)
              }
              className="inline-flex items-center gap-1 rounded-md bg-pb-accent px-2 py-1 text-dense-1 font-medium text-pb-accent-foreground transition-colors hover:bg-pb-accent-hover active:bg-pb-accent-active"
            >
              <Check className="h-3 w-3" />
              {isCanvasProposal ? "Approve all" : "Approve"}
            </button>
          )}
          {canReject && (
            <button
              type="button"
              onClick={() =>
                onAction?.(`toolProposal:reject:${payload.proposalId}`)
              }
              className="inline-flex items-center gap-1 rounded-md border border-pb-control-border bg-pb-card px-2 py-1 text-dense-1 font-medium text-pb-text-secondary hover:text-pb-text hover:border-pb-iris/40 transition-colors"
            >
              <X className="h-3 w-3" />
              Reject
            </button>
          )}
        </div>
      )}
    </ProposalCardFrame>
  );
}

/**
 * The compact card for a proposed external write.
 *
 * Deliberately shows no arguments at all. The generic card renders a preview
 * blob because a canvas proposal's shape is the product's own; an integration
 * proposal's is a remote provider's, and the reviewed, bounded rendering of it
 * lives in the review dialog. Here the user gets what they need to decide
 * whether to open that: which service, which target, how much it touches, and
 * what the governed risk is.
 */
function IntegrationProposalCard({
  payload,
  canApprove,
  canReject,
  onAction,
}: {
  payload: PlayerAIToolProposalPayload;
  canApprove: boolean;
  canReject: boolean;
  onAction?: PlayerAIWidgetActionHandler;
}): ReactElement {
  const facts = [
    payload.service != null && payload.service in INTEGRATION_SERVICE_LABEL
      ? INTEGRATION_SERVICE_LABEL[payload.service]
      : "Connected service",
    payload.targetLabel,
    payload.affectedCount !== undefined
      ? `${payload.affectedCount} item${payload.affectedCount === 1 ? "" : "s"}`
      : undefined,
  ].filter((fact): fact is string => Boolean(fact));

  return (
    <ProposalCardFrame payload={payload}>
      <div className="mt-1 text-dense-2 text-pb-text-muted">
        {facts.join(" · ")}
      </div>
      {payload.riskLabel ? (
        <div className="mt-1.5 inline-flex items-center gap-1 rounded border border-pb-amber/30 px-1.5 py-0.5 text-dense-2 text-pb-amber-text">
          <ShieldCheck className="h-3 w-3" aria-hidden />
          {payload.riskLabel}
        </div>
      ) : null}
      {payload.error ? (
        <div className="mt-2 rounded border border-pb-red/30 bg-pb-red-soft px-2 py-1.5 text-dense-1 leading-snug text-pb-red-text">
          {payload.error}
          {payload.errorDetails ? (
            <div className="mt-1.5">
              <CopyErrorDetailsButton
                details={payload.errorDetails}
                className="inline-flex items-center gap-1 rounded border border-pb-red/30 px-1.5 py-0.5 text-dense-2 text-pb-red-text transition-colors hover:bg-pb-red/10"
              />
            </div>
          ) : null}
        </div>
      ) : null}
      {/* THE RECOVERY STATE, and it REPLACES the controls rather than joining
          them. An approval batch is atomic, so one member this bundle cannot
          render makes the whole batch unreviewable — and review, approve and
          reject then all funnel into a silent no-op, leaving the batch pending
          and the Run blocked with nothing the user can press.

          One action, and it is not "approve anyway": a user must not be asked
          to agree to a write they cannot inspect. Nor is the fallback to dump
          the raw arguments, which would put a remote server's payload in front
          of them as the thing they are agreeing to. */}
      {payload.unsupportedReviewForm ? (
        <div className="mt-2 space-y-2">
          <p className="text-dense-1 leading-snug text-pb-text-secondary">
            These suggestions cannot be reviewed in their current format.
          </p>
          <button
            type="button"
            onClick={() =>
              onAction?.(`toolProposal:regenerate:${payload.proposalId}`)
            }
            className="inline-flex items-center gap-1 rounded-md bg-pb-accent px-2 py-1 text-dense-1 font-medium text-pb-accent-foreground transition-colors hover:bg-pb-accent-hover active:bg-pb-accent-active"
          >
            Regenerate suggestions
          </button>
        </div>
      ) : null}
      <div className="mt-2 flex flex-wrap gap-1.5">
        {payload.canReview &&
        payload.status !== "expired" &&
        !payload.unsupportedReviewForm ? (
          <button
            type="button"
            onClick={() =>
              onAction?.(`toolProposal:review:${payload.proposalId}`)
            }
            className="inline-flex items-center gap-1 rounded-md bg-pb-accent px-2 py-1 text-dense-1 font-medium text-pb-accent-foreground transition-colors hover:bg-pb-accent-hover active:bg-pb-accent-active"
          >
            {payload.reviewLabel ?? "Review suggestion"}
          </button>
        ) : null}
        {/* Approve stays available on the card for the unchanged case, so the
            common path does not require opening a dialog to agree. */}
        {canApprove ? (
          <button
            type="button"
            onClick={() =>
              onAction?.(`toolProposal:approve:${payload.proposalId}`)
            }
            className="inline-flex items-center gap-1 rounded-md border border-pb-control-border bg-pb-card px-2 py-1 text-dense-1 font-medium text-pb-text-secondary transition-colors hover:border-pb-iris/40 hover:text-pb-text"
          >
            <Check className="h-3 w-3" aria-hidden />
            Approve
          </button>
        ) : null}
        {canReject ? (
          <button
            type="button"
            onClick={() =>
              onAction?.(`toolProposal:reject:${payload.proposalId}`)
            }
            className="inline-flex items-center gap-1 rounded-md border border-pb-control-border bg-pb-card px-2 py-1 text-dense-1 font-medium text-pb-text-secondary transition-colors hover:border-pb-iris/40 hover:text-pb-text"
          >
            <X className="h-3 w-3" aria-hidden />
            Reject
          </button>
        ) : null}
      </div>
    </ProposalCardFrame>
  );
}

/* ---------------- Run activity ---------------- */

/**
 * Fallback copy when a source no longer resolves to an authorized name. The
 * kind still describes what was used, so the sentence stays accurate without
 * naming anything.
 */
const UNRESOLVED_SOURCE_LABEL: Record<keyof PlayerAIUnresolvedSources, string> =
  {
    attachment: "an attached file",
    preparationSource: "a Preparation source",
  };

/** Fallback labels, one per unresolved source, in a deterministic order. */
function unresolvedSourceLabels(
  unresolved: PlayerAIUnresolvedSources | undefined,
): string[] {
  if (!unresolved) return [];
  return (
    Object.keys(UNRESOLVED_SOURCE_LABEL) as Array<
      keyof PlayerAIUnresolvedSources
    >
  ).flatMap((kind) =>
    Array.from(
      { length: Math.max(0, unresolved[kind] ?? 0) },
      () => UNRESOLVED_SOURCE_LABEL[kind],
    ),
  );
}

type ActivityCopy = {
  readonly active: (subject: string | null) => string;
  readonly done: (subject: string | null) => string;
  readonly failed: (subject: string | null) => string;
};

/**
 * Display names for the closed service set. Allowlisted here, in the design
 * system, so no remote catalogue entry or tool name can supply the words.
 */
const INTEGRATION_SERVICE_LABEL: Record<PlayerAIIntegrationService, string> = {
  jira: "Jira",
  confluence: "Confluence",
};

/** What an unclassified or future service is called. Never a provider string. */
const GENERIC_SERVICE_LABEL = "a connected service";

function integrationServiceLabel(row: PlayerAIRunActivityRow): string {
  const service = row.service;
  return service != null && service in INTEGRATION_SERVICE_LABEL
    ? INTEGRATION_SERVICE_LABEL[service]
    : GENERIC_SERVICE_LABEL;
}

/**
 * The only place activity copy is decided. Three rules make it truthful:
 * `preparing_canvas_proposal` never claims a change happened,
 * `applying_approved_changes` only reads "Canvas updated." once the owning
 * backend has confirmed the write (i.e. status is completed), and the
 * integration kinds name the SERVICE and never the tool, the object or the
 * arguments.
 */
const RUN_ACTIVITY_COPY: Record<KnownPlayerAIRunActivityKind, ActivityCopy> = {
  ...TOOL_ACTIVITY_COPY,
  reading_document_section: {
    active: (subject) =>
      subject
        ? `Reading a section of ${subject}…`
        : "Reading a section of an attached file…",
    done: (subject) =>
      subject
        ? `Read a section of ${subject}`
        : "Read a section of an attached file",
    failed: (subject) =>
      subject
        ? `Couldn’t read a section of ${subject}`
        : "Couldn’t read a section of an attached file",
  },
  checking_documents: {
    active: () => "Checking available files…",
    done: () => "Checked available files",
    failed: () => "Couldn’t check the available files",
  },
  searching_documents: {
    active: (subject) =>
      subject ? `Searching ${subject}…` : "Searching your files…",
    done: (subject) =>
      subject ? `Searched ${subject}` : "Searched your files",
    failed: (subject) =>
      subject
        ? `Couldn’t search ${subject}`
        : "Couldn’t search an attached file",
  },
  reading_document: {
    active: (subject) =>
      subject ? `Reading ${subject}…` : "Reading an attached file…",
    done: (subject) => (subject ? `Read ${subject}` : "Read an attached file"),
    failed: (subject) =>
      subject ? `Couldn’t read ${subject}` : "Couldn’t read an attached file",
  },
  reviewing_canvas: {
    active: () => "Reading the canvas…",
    done: () => "Read the canvas",
    failed: () => "Couldn’t read the canvas",
  },
  preparing_answer: {
    active: () => "Preparing an answer…",
    done: () => "Prepared an answer",
    failed: () => "Couldn’t prepare an answer",
  },
  // A failure here costs a compaction, never the answer: the thread keeps its
  // previous context and carries on, so the copy says what was not done rather
  // than implying the turn is lost.
  compacting_context: {
    active: () => "Compacting conversation context…",
    done: () => "Context compacted",
    failed: () => "Couldn’t compact the context — continuing with what’s here",
  },
  preparing_canvas_proposal: {
    active: () => "Preparing suggested canvas changes…",
    done: () => "Suggested changes are ready",
    failed: () => "Couldn’t prepare the suggested changes",
  },
  applying_approved_changes: {
    active: () => "Applying approved changes…",
    done: () => "Canvas updated",
    failed: () => "Couldn’t apply the approved changes",
  },
  // One coarse read state for every governed integration. MCP servers do not
  // reliably distinguish a search from a fetch, so claiming the difference
  // would be a guess presented as fact.
  //
  // `subject` is never null for these three — `activitySubject` resolves the
  // service label or the generic one — so none of them defaults it. Every
  // sentence is also built with the service in OBJECT position ("Updated Jira",
  // not "Jira updated"), because the generic label is lower-case by design and
  // would otherwise open a sentence in lower case.
  reading_integration: {
    active: (service) => `Reading ${service}…`,
    done: (service) => `Read ${service}`,
    failed: (service) => `Couldn’t read ${service}`,
  },
  preparing_integration_change: {
    active: (service) => `Preparing a change in ${service}…`,
    done: (service) => `Suggested change for ${service} is ready`,
    failed: (service) => `Couldn’t prepare the change for ${service}`,
  },
  applying_integration_change: {
    active: (service) => `Applying the approved change in ${service}…`,
    done: (service) => `Updated ${service}`,
    failed: (service) => `Couldn’t apply the approved change in ${service}`,
  },
};

/**
 * What a kind this bundle does not know reads as.
 *
 * It states that work happened and refuses to say what — the only honest thing
 * an older bundle can say about a newer service's activity. Rendering nothing
 * would be worse: the row exists precisely because the assistant is busy, and
 * dropping it makes an active Run look idle.
 */
const UNKNOWN_ACTIVITY_COPY: ActivityCopy = {
  active: () => "Working on your request…",
  done: () => "Finished a step",
  failed: () => "A step didn’t finish",
};

/** Which kinds describe a governed integration rather than a file or canvas. */
const INTEGRATION_ACTIVITY_KINDS: ReadonlySet<string> = new Set([
  "reading_integration",
  "preparing_integration_change",
  "applying_integration_change",
]);

function activitySubject(row: PlayerAIRunActivityRow): string | null {
  if (
    row.kind === "reading_widget" ||
    row.kind.startsWith("preparing_widget_") ||
    row.kind === "applying_widget_change"
  ) {
    return row.widgetLabel && ACTIVITY_WIDGET_LABELS.has(row.widgetLabel)
      ? row.widgetLabel
      : null;
  }
  if (INTEGRATION_ACTIVITY_KINDS.has(row.kind)) {
    return integrationServiceLabel(row);
  }
  const subjects = [
    ...(row.sourceNames ?? []),
    ...unresolvedSourceLabels(row.unresolvedSources),
  ];
  if (subjects.length === 0) return null;
  if (subjects.length > 1) return `${subjects.length} files`;
  return subjects[0] ?? null;
}

export function runActivityLabel(row: PlayerAIRunActivityRow): string {
  // Registry LOOKUP, not indexing: `kind` is deliberately wider than the map's
  // keys, so an additive kind resolves to `undefined` here and must fall back
  // rather than reaching `.failed` on nothing and throwing inside a render.
  const copy =
    RUN_ACTIVITY_COPY[row.kind as KnownPlayerAIRunActivityKind] ??
    UNKNOWN_ACTIVITY_COPY;
  const subject = activitySubject(row);
  const base =
    row.status === "failed"
      ? copy.failed(subject)
      : row.status === "completed"
        ? copy.done(subject)
        : copy.active(subject);
  // A count is shown only when it is greater than one. A folded row standing
  // for a single operation is just that operation, and "· 1 operations" would
  // be noise that reads as a bug.
  const repeats = row.repeatCount ?? 1;
  if (!Number.isFinite(repeats) || repeats <= 1) return base;
  const suffix = `· ${Math.floor(repeats)} operations`;
  // The count goes INSIDE the trailing ellipsis, so a grouped active row still
  // reads as one ongoing step rather than as a finished one with a suffix.
  return base.endsWith("…")
    ? `${base.slice(0, -1)} ${suffix}…`
    : `${base} ${suffix}`;
}

/**
 * Compact activity timeline for the active assistant turn.
 *
 * State is never carried by colour alone — each row pairs its tone with a
 * distinct icon and its own wording. The spinner drops its keyframes under
 * `prefers-reduced-motion`, and only the newest active line is announced so a
 * screen reader is not re-read the whole history on every transition.
 */
export function RunActivityWidget({
  payload,
}: {
  payload: PlayerAIRunActivityPayload;
}) {
  const announced = [...payload.rows]
    .reverse()
    .find((row) => row.status === "started");
  /* The run is a compact Timeline (OK-55) in the recessed Card inset sm
     frame (OK-22): a Spinner marker while a step runs, a check or a warning
     glyph once it settles. The label truncates and shows in full in the
     Tooltip while clipped. */
  return (
    <Card variant="inset" size="sm" className="mt-2">
      <CardBody>
        <Timeline
          density="compact"
          items={payload.rows.map((row) => ({
            id: row.id,
            title: <TruncatedText>{runActivityLabel(row)}</TruncatedText>,
            markerSlot:
              row.status === "started" ? (
                <Spinner size="xs" tone="ai" aria-hidden />
              ) : row.status === "completed" ? (
                <Check
                  className="h-3 w-3 shrink-0 text-pb-green-text"
                  aria-hidden
                />
              ) : (
                <TriangleAlert
                  className="h-3 w-3 shrink-0 text-pb-amber-text"
                  aria-hidden
                />
              ),
          }))}
        />
        {/* DS keep (PR 3): a screen-reader-only live region, not a status
            indicator. */}
        <span role="status" aria-live="polite" className="sr-only">
          {announced ? runActivityLabel(announced) : ""}
        </span>
      </CardBody>
    </Card>
  );
}

/* ---------------- Document grounding ---------------- */

export function DocumentGroundingWidget({
  payload,
}: {
  payload: PlayerAIDocumentGroundingPayload;
}) {
  const names = [
    ...payload.sourceNames,
    ...unresolvedSourceLabels(payload.unresolvedSources),
  ];
  if (names.length === 0) return <></>;
  const maxVisible = Math.max(1, payload.maxVisible ?? 3);
  const visible = names.slice(0, maxVisible);
  const overflow = names.length - visible.length;
  return (
    <div className="mt-2 flex min-w-0 items-center gap-1.5 text-dense-1 text-pb-text-muted">
      <FileText className="h-3 w-3 shrink-0" aria-hidden />
      <span className="min-w-0 truncate" title={names.join(", ")}>
        Grounded with {visible.join(", ")}
        {overflow > 0 ? ` +${overflow} more` : ""}
      </span>
      {/* The claim is retrieval use, not a page, quote, or truth certificate. */}
      <span className="sr-only">
        This answer used content retrieved from {names.join(", ")}.
      </span>
    </div>
  );
}

/* ---------------- Video ---------------- */

/** Inline HTML5 player (ported from the DS reference). Tokens, not raw
 * colours: the letterbox + control scrim use pb-overlay so all three themes
 * resolve correctly. */
export function VideoWidget({ payload }: { payload: PlayerAIVideoPayload }) {
  const videoRef = useRef<HTMLVideoElement>(null);

  const onFullscreen = (): void => {
    const el = videoRef.current;
    if (!el) return;
    if (typeof el.requestFullscreen === "function") {
      void el.requestFullscreen();
    } else if (
      "webkitEnterFullscreen" in el &&
      typeof (el as { webkitEnterFullscreen?: () => void })
        .webkitEnterFullscreen === "function"
    ) {
      // iOS Safari only exposes fullscreen on the <video> element itself.
      (el as { webkitEnterFullscreen: () => void }).webkitEnterFullscreen();
    }
  };

  /* The recessed widget frame is Card inset sm (OK-22, SN-07). */
  return (
    <Card variant="inset" size="sm" className="mt-2">
      <CardBody>
        {payload.title && <FrameTitle>{payload.title}</FrameTitle>}
        {/* DS keep (PR 3): the video player surface is media, not a DS part. */}
        <div className="relative w-full overflow-hidden rounded bg-pb-overlay">
          {/* Captions are upstream metadata the payload cannot carry yet —
            tracked with the Player AI service contract. */}
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <video
            ref={videoRef}
            src={payload.url}
            controls
            playsInline
            preload="metadata"
            className="block w-full h-auto max-h-72 rounded"
          />
          <Tooltip
            content="Full screen"
            describeChild={false}
            wrapperClassName="absolute top-1.5 end-1.5"
          >
            <button
              type="button"
              onClick={onFullscreen}
              aria-label="Full screen"
              className="w-7 h-7 flex items-center justify-center rounded bg-pb-overlay/55 text-pb-text hover:bg-pb-overlay/75 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pb-iris"
            >
              <Maximize2 className="w-3.5 h-3.5" />
            </button>
          </Tooltip>
        </div>
      </CardBody>
    </Card>
  );
}

/* ---------------- Dispatcher ---------------- */

/**
 * Inline-widget registry — a `Record<kind, renderer>` rather than a switch
 * (root CLAUDE.md / packages/ui invariant #3: "Registries, not switches").
 * The mapped type narrows each renderer's `payload` to its own variant and
 * forces the map to stay total: adding a kind to `PlayerAIWidgetPayload`
 * without a renderer here is a compile error. `quickReply`/`toolProposal`
 * carry `onAction` through; the rest ignore the second argument.
 */
type InlineWidgetRenderer<K extends PlayerAIWidgetPayload["kind"]> = (
  payload: Extract<PlayerAIWidgetPayload, { kind: K }>,
  onAction?: PlayerAIWidgetActionHandler,
) => ReactElement;

const INLINE_WIDGETS: {
  [K in PlayerAIWidgetPayload["kind"]]: InlineWidgetRenderer<K>;
} = {
  kpiList: (payload) => <KpiListWidget payload={payload} />,
  taskList: (payload) => <TaskListWidget payload={payload} />,
  signal: (payload) => <SignalWidget payload={payload} />,
  quickReply: (payload, onAction) => (
    <QuickReplyWidget
      payload={payload}
      {...(onAction !== undefined ? { onAction } : {})}
    />
  ),
  toolProposal: (payload, onAction) => (
    <ToolProposalWidget
      payload={payload}
      {...(onAction !== undefined ? { onAction } : {})}
    />
  ),
  actionBatch: (payload, onAction) => (
    <ActionBatchWidget
      payload={payload}
      {...(onAction !== undefined ? { onAction } : {})}
    />
  ),
  runActivity: (payload) => <RunActivityWidget payload={payload} />,
  documentGrounding: (payload) => <DocumentGroundingWidget payload={payload} />,
  video: (payload) => <VideoWidget payload={payload} />,
};

export function InlineWidget({
  payload,
  onAction,
}: {
  payload: PlayerAIWidgetPayload;
  onAction?: PlayerAIWidgetActionHandler;
}) {
  // Indexing with the union key yields a union of renderers; the discriminant
  // guarantees `payload` matches its own entry, so the widening cast is sound.
  const render = INLINE_WIDGETS[payload.kind] as
    | InlineWidgetRenderer<PlayerAIWidgetPayload["kind"]>
    | undefined;
  // Defensive: an unknown kind (data-driven payload) renders nothing, matching
  // the previous switch's `default: return null`.
  return render ? render(payload, onAction) : null;
}
